import { useEffect } from 'react';
import { Appearance, Platform } from 'react-native';
import { UnistylesRuntime } from 'react-native-unistyles';
import useColorScheme from '@/hooks/useColorScheme';
import useSetAppBackground from '@/hooks/useSetAppBackground';
import type { ThemeMode } from '@/stores/useThemeStore';
import useThemeStore from '@/stores/useThemeStore';

export default function useInitializeTheme() {
  const preferredColorScheme = useColorScheme();
  const theme = useThemeStore((state) => state.theme);
  const setRealTheme = useThemeStore((state) => state.setRealTheme);
  const webThemeOverride = getWebThemeOverride();
  const systemTheme =
    preferredColorScheme === 'light' || preferredColorScheme === 'dark'
      ? preferredColorScheme
      : 'dark';
  const selectedTheme = webThemeOverride ?? resolveTheme(theme, systemTheme);

  useSetAppBackground(selectedTheme);

  useEffect(() => {
    if (Platform.OS !== 'web') {
      Appearance.setColorScheme(theme === 'system' ? 'unspecified' : theme);
    }
  }, [theme]);

  useEffect(() => {
    setRealTheme(selectedTheme);
    UnistylesRuntime.setTheme(selectedTheme);
  }, [selectedTheme, setRealTheme]);
}

function resolveTheme(
  theme: ReturnType<typeof useThemeStore.getState>['theme'],
  systemTheme: ThemeMode,
) {
  if (theme === 'light' || theme === 'dark') {
    return theme;
  }

  return systemTheme;
}

function getWebThemeOverride(): ThemeMode | null {
  if (Platform.OS !== 'web') {
    return null;
  }

  const themeParam = new URLSearchParams(window.location.search).get('theme');

  if (themeParam === 'light' || themeParam === 'dark') {
    return themeParam;
  }

  return null;
}
